import { Basic } from "../basic.ts";
import { deepCompare } from "../../lib/utils.js";

/**
 * @class MSWindow
 * @extends Basic
 * @classdesc Окно
 */
export class MSWindow extends Basic {
    static get observedAttributes() {
        return super.observedAttributes.concat([
            "template",
            "typeid",
            "position",
            "proportiontype",
            "typed",
            "parameters"
        ]);
    }
    constructor() {
        super();
        const shadowRoot = this.attachShadow({ mode: "open" });
        // if (document.body.querySelector('ms-window') == this) {
        //     this.width = window.innerWidth;
        //     this.height = window.innerHeight;
        // }
        this._cacheClass = {};
        this._initParams = new Map();

        shadowRoot.innerHTML = `
            <slot></slot>
        `;

        this.setInitialBordersValues();

        this.eventListener = this.eventListener.bind(this);
        window.addEventListener("ms_message", this.eventListener);
        this.contentMaxZIndex = 0;
        this.style.boxSizing = "border-box";

        if (this.getAttribute("needtransformdiv")) {
            this.style.overflow = "visible";
            this.addTransformationAngleContainer();
            this.addTransformationScaleContainer();
        }

        this.initBaseParams();
        this.mergeLocationParams();
    }

    setInitialBordersValues() {
        this.style.borderWidth = 0;
        this.style.setProperty("--borderThickness", "1px");
        this.style.setProperty("--borderStyle", "solid");
        this.style.setProperty("--borderColor", "GRAY");
    }

    setDefaultParams() {
        //base params
        if (this._shadowcolor == null) 
            this.shadowcolor = "rgb(240,240,240)";
        if (this._backgroundtile == null) 
            this.backgroundtile = 2;
        if (this._horizontalalign == null) 
            this.horizontalalign = 1;
        if (this._verticalalign == null) 
            this.verticalalign = 1;
        if (this._dockstyle == null) 
            this.dockstyle = 3;
        if (this._showElement == null)
            this.isvisible = true;
        if (this._isenabled == null) 
            this.isenabled = true;

        if (this._proportiontype == null)
            this.proportiontype = 4;
        if (this._backgroundcolor == null)
            this.backgroundcolor = "rgb(245,240,245)";
        if (this._borderthickness == null)
            this.borderthickness = 1;
        if (this._borderstyle == null)
            this.borderstyle = 0;
        if (this._bordercolor == null)
            this.bordercolor = "gray";
    }

    setDefaultParamsInConstructor() {
        //Called from mergeParam
    }

    eventListener(message) {
        const data = message.data;
        switch (data.event) {
            case "afterswitch":
                this.changeUser();
                break;
        }
    }

    mergeLocationParams() {
        const search = location.search;
        const windowId = search.substring(1, search.indexOf("&"));
        if (this.id !== windowId) {
            return;
        }

        const urlSearchParams = new URLSearchParams(search);
        const locationProps = ["objectid", "outerpath", "taskid"];
        for (const prop of locationProps) {
            const val = urlSearchParams.get(prop);
            val && this.setAttribute(prop, val);
        }
        const p = new URLSearchParams(location.search).get("parameters");
        if (p) this.parameters = decodeURIComponent(p);
    }

    initBaseParams() {
        const def = $WinDefs.winDef[this.typeid];
        if (def) {
            if (def.actionlist)
                this.actionlist = JSON.stringify(JSON.parse(def.actionlist));
            if (def.windowTriggers)
                this.windowTriggers = JSON.parse(JSON.stringify(def.windowTriggers));
        }
    }

    mergeParam() {
        if (this._parameters) {
            try {
                const params = JSON.parse(this._parameters);
                const keys = Object.keys(params);
                for (const key of keys) {
                    const data = params[key];
                    if (this[key] == null)
                        this.attributeChangedCallback(key, this[key], data, null, {dontSendToServer:true});
                }
            } catch (e) {
                console.log('Error in set parameters for win ' + this.id, e);
            }
        }

        const def = $WinDefs.winDef[this.typeid];
        if (def) {
            for (const key in def) {
                if (key === "actionlist" || key === "windowTriggers")
                    continue;
                if (this[key] == null) 
                {
                    try {
                        const val = JSON.parse(JSON.stringify(def[key]));
                        this.attributeChangedCallback(key, this[key], val, null, {dontSendToServer:true});
                    } catch (e) {
                        console.log(`Error in set parameter ${key}=${def[key]} for win ${this.id}`, e);
                    }
                }
            }
        }

        this.setDefaultParams();

        const initParams = this._initParams;
        this._initParams = null;
        if (initParams) {
            for (const [key, value] of initParams)
                this[key] = value;
        }

        this.isvisible = this.isvisible;
        if (!this.isenabled) {
            this.disableElement();
        }
    }

    changeUser() {
        if (this.isenabled && this._maskElement) {
            this.isenabled = true;
        }
        $pm.controlEnable(this.childNodes);
    }

    get isContainer() {
        return true;
    }

    isPopup() {
        return this.parentNode && this.parentNode.nodeName === "MS-POPUP";
    }

    get template() {
        return this._templateId;
    }
    set template(value) {
        this._templateId = value;
        const template = document.querySelector(`template#${value}`);
        if (template) {
            // parentElement может быть равен null, если элемент склонирован, но не вставлен в DOM,
            // такое может быть в panelstask и для этого случая не нужно проверять isMainWindowCanBeRendered
            if (this.parentElement?.nodeName === "BODY") {
                if (this.isMainWindowCanBeRendered) {
                    this.innerHTML = template.innerHTML;
                }
            } else {
                this.insertTemplateHTML(template.innerHTML);
                this._calcScale();
            }
        } else if ($WinDefs.winDef[this.id]) {
            const win = JSON.parse(JSON.stringify($WinDefs.winDef[this.id]));
            this._isWinDefTemplate = true;
            for (const key in win) {
                if (win.hasOwnProperty(key)) {
                    if (key !== "template") {
                        this[key] = win[key];
                    }
                }
            }
            this._isWinDefTemplate = false;
            this._connectedWindow();
        }
    }

    insertTemplateHTML(templateHTML) {
        if (this.transformationContainerScale) {
            this.transformationContainerScale.innerHTML = templateHTML;
        } else {
            this.innerHTML = templateHTML;
            this.addBorders(this);
        }
    }

    renderMainWindow() {
        if (this.isMainWindowCanBeRendered) {
            return;
        }
        this.isMainWindowCanBeRendered = true;
        this.template = this.template;
        this.isMainWindowCanBeRendered = false;
    }

    addBorders(container) {
        const commonStyle = `
            box-sizing: border-box;
            position: absolute;
            z-index: var(--zIndex);
            border-style: var(--borderStyle);
            border-color: var(--borderColor);
        `;

        const sides = {
            top: `
                left: 0;
                top: 0;
                width: 100%;
                border-width: var(--borderThickness) 0 0 0;
            `,
            bottom: `
                left: 0;
                bottom: 0;
                width: 100%;
                border-width: 0 0 var(--borderThickness) 0;
            `,
            left: `
                top: 0;
                left: 0;
                height: 100%;
                border-width: 0 0 0 var(--borderThickness);
            `,
            right: `
                top: 0;
                right: 0;
                height: 100%;
                border-width: 0 var(--borderThickness) 0 0;
            `
        };

        const borders = Object.entries(sides).map(([_, sideStyle]) => {
            const div = document.createElement("div");
            div.style.cssText = commonStyle + sideStyle; // Поскольку рамочки теперь за пределами shadowRoot, то мы не можем импортировать файл border.css,
            // т.к. его стили будут доступны во всём DOM и перебивать стили в других компонентах. Поэтому нужно применять инлайновые стили - они не утекут в глобальный DOM
            return div;
        });
        container.append(...borders);
    }

    get proportiontype() {
        return this._proportiontype;
    }
    set proportiontype(value) {
        this._proportiontype = Number(value);
        this._calcScale();
    }

    get parameters() {
        return this._parameters;
    }
    set parameters(value) {
        this._parameters = value;
    }

    get angle() {
        return this._rotate ?? 0;
    }
    set angle(value) {
        this._rotate = +value;
        this._calcScale();
    }

    get scalex() {
        return this._scalex ?? 1;
    }
    set scalex(value) {
        this._scalex = +value;
        this._calcScale();
    }

    get scaley() {
        return this._scaley ?? 1;
    }
    set scaley(value) {
        this._scaley = +value;
        this._calcScale();
    }

    /**
     * Ширина
     * @type {number}
     */
    get width() {
        return this._width;
    }
    set width(value) {
        super.width = value;
        if (this.isPopup()) {
            this.parentNode.onResizeContent();
        }
        this._calcScale();
    }
    /**
     * Высота
     * @type {number}
     */
    get height() {
        return this._height;
    }

    set height(value) {
        super.height = value;
        if (this.isPopup()) {
            this.parentNode.onResizeContent();
        }
        this._calcScale();
    }

    /**
     * Видимость
     * @type {bool}
     */
    get isvisible() {
        return this._showElement;
    }
    set isvisible(value) {
        this._showElement = value;
        const isShow =
            (this.opacity === "" || this._toBool(this.opacity)) &&
            this._toBool(value);
        if (!this._isInited) {
            this.style.visibility = isShow ? "" : "hidden";
        } else {
            if (this.style.visibility === "hidden") this.style.visibility = "";
            if (this._redefinedStyleDisplay != null)
                this._redefinedStyleDisplay = isShow ? "flex" : "none";
            else
                this.style.display = isShow ? "flex" : "none";
        }
    }

    _initSkin() {
        this._main = this.shadowRoot.querySelector("svg");
    }

    setSvgParams(svgPath, value) {
        if (this.issvg) {
            let paths = svgPath.split(":");
            if (paths[0] === "svg") paths = paths.splice(1);
            this.svg(paths, value);
        }
    }

    svg(path, value) {
        const items = this._main.querySelectorAll(`[id=${path[0]}]`);
        if (items.length > 0) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                switch (path[1]) {
                    case "fill":
                        item.style.fill = value;
                        break;
                    case "Content":
                        item.textContent = value;
                        break;
                    case "class":
                        if (path[2]) {
                            if (this._cacheClass[path[2]]) {
                                item.classList.remove(
                                    this._cacheClass[path[2]]
                                );
                            }
                        } else {
                            while (item.classList.length > 0) {
                                item.classList.remove(item.classList.item(0));
                            }
                        }
                        if (value && value.trim().length > 0)
                            item.classList.add(value);
                        break;
                    default:
                        if (
                            path[0] == "feColorMatrixBodyFilter" ||
                            item.nodeName === "feColorMatrix"
                        ) {
                            const color = this._colorValues(value);
                            if (color) {
                                const r = (color[0] / 255).toFixed(2);
                                const g = (color[1] / 255).toFixed(2);
                                const b = (color[2] / 255).toFixed(2);
                                value = `${r} 0 0 0 0 0 ${g} 0 0 0 0 0 ${b} 0 0 0 0 0 ${color[3]} 0`;
                            }
                            // item.setAttribute(path[1], matrix);
                        }
                        if (item.attributes.getNamedItem(path[1])) {
                            item.setAttribute(path[1], value);
                        } else {
                            for (const key in this._cacheClass) {
                                const el = this._cacheClass[key];
                                if (el) {
                                    const items = this._main.querySelectorAll(
                                        `.${el}`
                                    );
                                    for (
                                        let index = 0;
                                        index < items.length;
                                        index++
                                    ) {
                                        items[index].classList.toggle(el);
                                        requestAnimationFrame(() => {
                                            items[index].classList.toggle(el);
                                        });
                                    }
                                }
                            }
                        }
                }
            }
            if (path[1] === "class" && path[2]) {
                if (value) {
                    this._cacheClass[path[2]] = value;
                }
            }
        } else if (path[0] === "AvaryBorder") {
            const style = document.createElement("style");
            style.type = "text/css";
            style.innerHTML =
                "#AvaryBorder {display:none;} #AvaryBorder.Alarm {display:block; stroke:rgb(255,0,0);} #AvaryBorder.Warning {display:block; stroke:rgb(255,170,0);}";
            this._main.appendChild(style);
            const rect = document.createElementNS(
                "http://www.w3.org/2000/svg",
                "rect"
            );
            rect.setAttribute("id", "AvaryBorder");
            let x, y;
            if (this._main.viewBox) {
                x = this._main.viewBox.baseVal.x;
                y = this._main.viewBox.baseVal.y;
            }
            rect.setAttribute("x", x || 0);
            rect.setAttribute("y", y || 0);
            rect.setAttribute("height", "100%");
            rect.setAttribute("width", "100%");
            rect.style.fill = "rgb(0,0,0)";
            rect.style.strokeWidth = "1";
            rect.style.fillOpacity = "0";
            rect.innerHTML = `<animate xmlns="http://www.w3.org/2000/svg" attributeName="opacity" attributeType="XML" begin="0s; animEnd.end" dur="1s" values="0;1;0" repeatCount="indefinite"/>`;
            this._main.appendChild(rect);
            this.svg(path, value);
        }
    }

    _calcScale() {
        if (!this._parametersInited)
            return;
        if (this.hasAttribute("root"))
            return;
        if (this._frameScaling)
            return; //Масштаб выставлен из контейнера

        const hasTypeId = this.typeid && this.typeid != this.id;
        if (!hasTypeId) {
            this.scaleDefault();
            return;
        }

        const { width, height } = $WinDefs.winDef[this.typeid];
        const { scaleX, scaleY } = this.calcScaleProportionType(+this.width, +this.height, +width, +height);
        const hasTransformation = this.scalex !== 1 || this.scaley !== 1 || this.angle !== 0;
        if (hasTransformation) {
            this.scaleDefault();
            this.updateTransformation(scaleX, scaleY);
        } else {
            if (scaleX != 1 || scaleY != 1)
                this.clearInnerTransformation(scaleX, scaleY, width, height);
            else
                this.clearInnerTransformation(1, 1, +this.width, +this.height);
        }
    }

    scaleDefault() {
        this.style.transform = `scale(${this.scalex}, ${this.scaley}) rotate(${this.angle}deg)`;
        this.style.transformOrigin = "center";
        if (this.width != null)
            this.style.width = this._setValueUnit(this.width);
        if (this.height != null)
            this.style.height = this._setValueUnit(this.height);
    }

    calcScaleProportionType(widthContainer, heightContainer, width, height){
        let scaleY, scaleX;
        switch (this.proportiontype) {
            case 0:
                scaleY = heightContainer / height;
                scaleX = widthContainer / width;
                break;
            case 1:
                scaleX = scaleY = widthContainer / width
                break;
            case 2:
                scaleX = scaleY = heightContainer / height
                break;
            case 3: {
                const coefficientWindow = width / height
                const coefficientContainer = widthContainer / heightContainer
                if(coefficientWindow > coefficientContainer){
                    scaleX = scaleY = heightContainer / height;
                } else {
                    scaleX = scaleY = widthContainer / width;
                }
                break;
            }
            case 4: {
                const coefficientWindow = width / height
                const coefficientContainer = widthContainer / heightContainer
                if(coefficientWindow < coefficientContainer){
                    scaleX = scaleY = heightContainer / height;
                } else {
                    scaleX = scaleY = widthContainer / width;
                }
                break;
            }
        }
        return { scaleX, scaleY };
    }

    _calcScaleRoot() {
        const { scaleX, scaleY } = this.calcScaleProportionType(document.documentElement.clientWidth, document.documentElement.clientHeight, +this.width, +this.height);
        this.style.transform = `scale(${scaleX}, ${scaleY})`;
        this.style.transformOrigin = "0 0";
    }

    updateTransformation(scaleX, scaleY) {
        if (!this.transformationContainerAngle) {
            console.log(`No transformation container for window ${this.id}`)
            return;
        }
        this.style.transform = `scale(${this.scalex}, ${this.scaley})`; // угол обязательно нужно очистить у window
        this.transformationContainerAngle.style.transform = `rotate(${this.angle}deg)`;
        this.transformationContainerScale.style.transform = `scale(${scaleX}, ${scaleY})`;
    }

    addTransformationAngleContainer() {
        this.transformationContainerAngle = document.createElement('div');
        const style = this.transformationContainerAngle.style;
        style.position = "absolute";
        style.width = "100%";
        style.height = "100%";
        style.transformOrigin = "center";
        style.overflow = "hidden";
        this.addBorders(this.transformationContainerAngle);
        this.appendChild(this.transformationContainerAngle);
    }

    addTransformationScaleContainer() {
        this.transformationContainerScale = document.createElement('div');
        const style = this.transformationContainerScale.style;
        style.position = "absolute";
        style.width = "100%";
        style.height = "100%";
        style.transformOrigin = "0 0";
        this.transformationContainerAngle.appendChild(this.transformationContainerScale);
    }

    clearInnerTransformation(scaleX, scaleY, width, height) {
        this.style.transform = `scale(${scaleX}, ${scaleY})`;
        this.style.transformOrigin = "0 0";
        this.style.width = this._setValueUnit(width);
        this.style.height = this._setValueUnit(height);

        if (this.transformationContainerAngle) {
            this.transformationContainerAngle.style.transform = "none";
            this.transformationContainerScale.style.transform = "none";
        }
    }

    scaleRootWindow() {
        let scale, scaleH, scaleW;
        const sh = Number(this.height);
        const sw = Number(this.width);
        scaleH = document.documentElement.clientHeight / sh;
        scaleW = document.documentElement.clientWidth / sw;

        scale = scaleH < scaleW ? scaleH : scaleW;

        window.__scale = {
            x: scale,
            y: scale,
        };

        const { heighttype, widthtype } = $WinDefs.winDef[this.id] || {};
        const winStyle = this.style;

        switch (true) {
            case heighttype === "0" && widthtype === "0": {
                winStyle.transform = "";
                winStyle.transformOrigin = "";
                winStyle.overflow = "auto";
                winStyle.width = "100%";
                winStyle.height = "100%";
                break;
            }
            case widthtype === "0": {
                winStyle.transform = `scaleY(${scaleH})`;
                winStyle.transformOrigin = "center 0";
                winStyle.overflowX = "overlay"; // deprecated, но работает, как надо
                winStyle.width = "100%";
                break;
            }
            case heighttype === "0": {
                winStyle.transform = `scaleX(${scaleW})`;
                winStyle.transformOrigin = "0 center";
                winStyle.overflowY = "overlay"; // deprecated, но работает, как надо
                winStyle.height = "100%";
                break;
            }
            default: {
                this._calcScaleRoot();
            }
        }
    }

    get typeid() {
        return this.getAttribute("typeid");
    }
    set typeid(value) {
        this.setAttribute("typeid", value);
    }

    get typed() {
        return this._typed;
    }
    set typed(value) {
        this._typed = value;
    }

    get position() {
        return this._position;
    }
    set position(value) {
        this._position = value;
        this.style.position = value;
    }

    get borderthickness() {
        return this._borderthickness;
    }

    set borderthickness(value) {
        value = parseInt(value, 10);
        if (isNaN(value) || value < 0) {
            return;
        }
        this._borderthickness = value;
        this.style.setProperty("--borderThickness", this._setValueUnit(value));
    }

    get bordercolor() {
        return this._bordercolor; // нельзя задавать значение по умолчанию, т.к. в mergeParam оно проверяется, и если пустое, то берётся из winDefs.
        // Это нужно для случаев, когда параметр изменён в типе окна.
    }

    set bordercolor(value) {
        this._bordercolor = value;
        value = this.tryToGetUserColor(value);
        this.style.setProperty("--borderColor", value);
    }

    get backgroundcolor() {
        return this._backgroundcolor;
    }

    set backgroundcolor(value) {
        super.backgroundcolor = value;

        if (this.transformationContainerAngle) {
            //Установка style.backgroundImage или style.backgroundColor не меняет background
            if (this.style.background)
                this.transformationContainerAngle.style.background = this.style.background;
            else if (this.style.backgroundImage)
                this.transformationContainerAngle.style.backgroundImage = this.style.backgroundImage;
            else
                this.transformationContainerAngle.style.backgroundColor = this.style.backgroundColor;
            this.style.background = "transparent";
        }
    }

    get borderstyle() {
        return this._borderstyle; // Если переопределяем сеттер, то нужно также переопределять и геттер родителя,
        // даже если геттер родителя полностью идентичный. Иначе this.borderstyle будет возвращать undefined, даже, если до этого мы ему присваивали значение.
    }

    set borderstyle(value) {
        this._borderstyle = value;
        this.style.setProperty("--borderStyle", this.getBorderStyle(window._enums.BorderStyleType[value]));
    }

    get shadowsize() {
        return this._shadowsize;
    }

    set shadowsize(value) {
        value = parseInt(value, 10);
        if (value >= 0) {
            this._shadowsize = value;

            if (this.transformationContainerAngle) {
                this.style.boxShadow = "none";
            }

            this._calcBlockShadow(this.transformationContainerAngle || this);
        }
    }

    get shadowcolor() {
        return this._shadowcolor;
    }

    set shadowcolor(value) {
        value = this.tryToGetUserColor(value);
        this._shadowcolor = value ? this._transformHexColor(value) : "";

        if (this.transformationContainerAngle) {
            this.style.boxShadow = "none";
        }

        this._calcBlockShadow(this.transformationContainerAngle || this);
    }

    get resource() {
        return this._resource;
    }
    set resource(value) {
        if (this._initParams)
        {
            this._initParams.set("resource", value);
            return;
        }

        if (this.transformationContainerAngle) {
            this.updateResource(value, this.transformationContainerAngle);
            this.style.backgroundPosition = "0 0";
            this.style.backgroundRepeat = "repeat";
            this.style.backgroundSize = "auto";
            this.style.backgroundImage = "none";
            return;
        }

        this.updateResource(value);
    }

    updateZIndex(value /*:Number*/) {
        if (value > this.contentMaxZIndex) this.contentMaxZIndex = value;
        if (this._maskElement) {
            this._maskElement.style.zIndex = this.contentMaxZIndex + 1;
        }
        this.style.setProperty("--zIndex", this.contentMaxZIndex);
    }

    disableElement() {
        if (this._maskElement || !this._isInited) return;
        const base = this.transformationContainerAngle || this.shRoot || this.shadowRoot;
        this._pointerEvents = this.style.pointerEvents;

        let zIndex = 0;
        this.childNodes.forEach(el => {
            if (Number(el.z) && zIndex < el.z) zIndex = Number(el.z);
        });
        const maskElement = this.createMaskElement(zIndex);

        base.append(maskElement);
        this._maskElement = maskElement;
    }

    connectedCallback() {
        this._openedEventCalled = false;
        if (!this._isWinDefTemplate) {
            this._connectedWindow();
            // super.connectedCallback();
            // const event = new CustomEvent('ms_message');
            // event.data = { event: 'windowStart', window: this };
            // window.dispatchEvent(event);
        }
        this._isInited = true;
    }

    afterInitialize() {
        this.mergeParam();

        this.updateEnableInputValueResetDelay();

        for (let i = 0; i < this.children.length; i++) {
            if (this.children[i].afterInitialize && (!this.children[i].canCallAfterInitialize || this.children[i].canCallAfterInitialize()))
                this.children[i].afterInitialize();
        }

        this._parametersInited = true;
        this._calcScale();

        if (!this._openedEventCalled) {
            this._openedEventCalled = true;
            let event = new CustomEvent("windowopened", {
                detail: {},
            });
            this.dispatchEvent(event);
        }
    }

    _connectedWindow() {
        if (!this._winStart) {
            this._winStart = true;
            const event = new CustomEvent("ms_message");
            event.data = { event: "windowStart", window: this };
            window.dispatchEvent(event);
        }
    }

    disconnectedCallback() {
        if (this._actionslist.windowclosing) {
            this.dispatchEvent(new CustomEvent("windowclosing", {
                detail: {
                    onCompleteCallback: () => {
                        // Навешанных на закрытие окна событий может быть несколько и нужно дождаться их выполнения,
                        // прежде, чем очищать объекты
                        this.removeWindow();
                    },
                },
            }));
            return;
        }

        this.removeWindow();
    }

    removeWindow() {
        this.innerHTML = "";
        this.shadowRoot.innerHTML = "";
        window.removeEventListener("ms_message", this.eventListener);
        this.eventListener = null;

        const event = new CustomEvent("ms_message");
        event.data = { event: "windowRemove", window: this };
        window.dispatchEvent(event);
        event.data.window = null;

        this.windowTriggers = null;
        this.isdisconnected = true;

        super.disconnectedCallback();
    }

    /**
     * Коллбек по изменению атрибутов элемента
     * @see https://w3c.github.io/webcomponents/spec/custom/
     * @param {string} attrName название атрибута
     * @param {*} oldVal старое значение атрибута
     * @param {*} newVal новое значение атрибута
     * @param {{
         *  forced: boolean, // изменять параметр принудительно (даже если новое значение не отличается от старого)
         *  link: boolean, // говорит о том, что параметр пришёл по связи
         *  linkids: string, // список Id окон, через параметры которых прошло значение (для избежания зацикливания)
         * }} [callbackData]
     */
    attributeChangedCallback(attrName, oldVal, newVal, itemIndex, callbackData ) {
        const forced = callbackData?.forced;
        const equalsValue = deepCompare(oldVal, newVal);
        if (forced || !equalsValue) {
            const struct = attrName.split(".");
            if (struct.length > 1) {
                const val =
                    newVal.dataType && newVal.value ? newVal.value : newVal;
                // this[attrName] = val;
                let value = this;
                const key = struct[0];
                if (this[key] == null && !this._parametersInited) {
                    try {
                        let data = null;
                        if (this._parameters) {
                            const params = JSON.parse(this._parameters);
                            data = params[key];
                        }
                        if (data == null) {
                            const def = $WinDefs.winDef[this.typeid];
                            if (def && def[key] != null) {
                                data = JSON.parse(JSON.stringify(def[key]));
                            }
                        }
                        if (data != null)
                            this.attributeChangedCallback(key, this[key], data, null, {dontSendToServer:true});
                    }
                    catch (e) {
                        console.log('Error in set parameters for win ' + this.id, e);
                    }
                }

                for (let i = 0; i < struct.length - 1; i++) {
                    value = value?.[struct[i]];
                }
                const keys = Object.keys(value || {});
                const i = keys.findIndex(
                    el => el === struct[struct.length - 1] || el.toLowerCase() === struct[struct.length - 1]  //TODO Проверку toLowerCase убрать потом
                );

                if (value && value[keys[i]] != val) {
                    value[keys[i]] = val;
                    this.callBackWithCycleControl(struct[0], this[struct[0]], callbackData, true);
                }
            } else {
                const val =
                    newVal && newVal.dataType && newVal.value
                        ? newVal.value
                        : newVal;

                if (!equalsValue || forced) {
                    this[attrName] = val;
                    this.callBackWithCycleControl(attrName, val, callbackData, true);
                }
            }
        }
    }
}